import { useEffect, useState } from 'react'
import { Show, UserButton, useAuth } from '@clerk/react'
import { Link } from 'react-router-dom'
import { getActivePod } from '@/lib/pod'
import StaffSignIn from '@/components/StaffSignIn'
import { isOwnerClaim } from '@/lib/staff-role'
import { TabButton, PendingPanel, AnsweredPanel } from '@/components/QuestionQueue'
import type { QuestionStatus } from '@/lib/api'

/**
 * THE KELVO OWNER'S QUESTIONS PAGE — /questions on a staff build.
 *
 * Where the owner answers what staff asked and the office had not written
 * down. Each answer becomes an approved, dated fact the assistant uses from
 * then on (a pod on the facts corpus mints a fact on answer — see the answer
 * route in workers/api.ts), and it carries a re-confirmation date so it cannot
 * quietly go stale.
 *
 * THE SAME QUEUE AS FOREDESK'S, NOT A COPY OF IT: src/components/QuestionQueue
 * is shared. The differences are stated as props: answering by voice is done
 * ON THIS PAGE, on the owner's session (`voiceNotes`, Build 10b — the words
 * come back as a draft he edits before pressing Answer), not by the texted
 * link Foredesk uses; and the empty-state wording speaks of staff, not
 * customers.
 *
 * OWNER ONLY, ENFORCED BY THE WORKER. This page hides itself from anyone whose
 * session does not say owner, as a courtesy. The Worker's owner gate refuses
 * every /api/admin/* call from anyone else regardless of what this page shows.
 */
export default function OwnerQuestions() {
  const pod = getActivePod()
  const { isLoaded, sessionClaims } = useAuth()
  const [tab, setTab] = useState<QuestionStatus>('pending')

  useEffect(() => {
    const previous = document.title
    document.title = `${pod.brandName} — questions from staff`
    return () => {
      document.title = previous
    }
  }, [pod.brandName])

  if (!isLoaded) {
    return (
      <div className="flex min-h-[100svh] items-center justify-center bg-surface-base">
        <p className="text-sm text-text-muted" role="status">
          Loading…
        </p>
      </div>
    )
  }

  return (
    <>
      <Show when="signed-out">
        <StaffSignIn returnTo="/questions" />
      </Show>

      <Show when="signed-in">
        <div className="min-h-[100svh] bg-surface-base">
          <header className="border-b border-border-subtle bg-surface-raised">
            <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-4 py-3">
              <div className="min-w-0">
                <p className="font-display font-semibold leading-tight tracking-tight truncate">
                  {pod.brandName}
                </p>
                <p className="text-xs text-text-muted leading-tight">Questions from staff</p>
              </div>
              <div className="flex items-center gap-3">
                <Link
                  to="/"
                  className="rounded-lg border border-border-subtle px-3 py-1.5 text-sm font-medium text-text-primary transition-colors hover:border-brand-accent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
                >
                  Back to {pod.assistantName}
                </Link>
                <UserButton />
              </div>
            </div>
          </header>

          <main className="mx-auto max-w-3xl px-4 py-8">
            {isOwnerClaim(sessionClaims) ? (
              <section>
                <h1 className="font-display text-2xl font-semibold tracking-tight">
                  What your staff asked
                </h1>
                <p className="mt-1 max-w-prose text-sm text-text-muted">
                  These are questions {pod.assistantName} could not answer because it is not written
                  down yet. Answer one once and {pod.assistantName} gives that answer from then on,
                  to anyone who asks. It will remind you to re-check it before it goes out of date.
                </p>

                <div className="mt-6 flex gap-1 border-b border-border-subtle">
                  <TabButton active={tab === 'pending'} onClick={() => setTab('pending')}>
                    Waiting
                  </TabButton>
                  <TabButton active={tab === 'answered'} onClick={() => setTab('answered')}>
                    Answered
                  </TabButton>
                </div>

                {/* Keyed so switching tabs remounts the panel and fetches fresh. */}
                {tab === 'pending' ? (
                  <PendingPanel
                    key="pending"
                    drawers
                    voiceNotes
                    emptyMessage="Nothing waiting. When staff ask something that isn't written down, it appears here."
                  />
                ) : (
                  <AnsweredPanel
                    key="answered"
                    emptyMessage="Nothing answered yet. Answers you give will appear here."
                  />
                )}
              </section>
            ) : (
              <section className="rounded-card border border-border-subtle bg-surface-raised p-6">
                <h1 className="font-display text-lg font-semibold tracking-tight">
                  This page is for the owner
                </h1>
                <p className="mt-2 text-sm text-text-muted">
                  Answering staff questions is done by the owner of {pod.brandName}. If you think you
                  should have access, speak to the office.
                </p>
              </section>
            )}
          </main>
        </div>
      </Show>
    </>
  )
}
